import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Stack, Typography, IconButton, Button, Divider} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {useParams} from 'react-router-dom';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {addFileTag, deleteFileTag, getFileTags, isUsedFileTag} from '@services/Files/FilesService';
import {FileTag, UsedFileTag} from '@pages/FilesPage';
import AddIcon from '@mui/icons-material/Add';
import {TagListRow} from './TagListRow';
import {SearchInput} from '@ui/SearchInput/SearchInput';
import {useTranslation} from 'react-i18next';

type FileUploadTagsSidebarProps = {
    isOpen: boolean;
    onClose: () => void;
    onSelectTags: (selectedTags: FileTag[]) => void;
    selectedTags: FileTag[];
    refetch?: () => void;
};

export const FileUploadTagsSidebar = ({
    isOpen,
    onClose,
    onSelectTags,
    selectedTags,
    refetch,
}: FileUploadTagsSidebarProps) => {
    const queryClient = useQueryClient();
    const params = useParams();

    const [localTags, setLocalTags] = useState<FileTag[]>(selectedTags);
    const [searchQuery, setSearchQuery] = useState('');
    const [usedTag, setUsedTag] = useState<UsedFileTag>({isUsed: false, documentCount: 0});

    const {t} = useTranslation('supervising');

    const {data: tags} = useQuery({
        queryKey: ['fileTags', params.wellId],
        queryFn: () => getFileTags(params.wellId || ''),
        enabled: !!params.wellId,
        gcTime: 0,
    });

    const addTagMutation = useMutation({
        mutationFn: (newTag: {title: string; wellId: string}) =>
            addFileTag(newTag.title, newTag.wellId),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['fileTags', params.wellId]});
        },
    });

    const deleteTagMutation = useMutation({
        mutationFn: (tagId: string) => deleteFileTag(tagId),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['fileTags', params.wellId]});
        },
    });

    useEffect(() => {
        setLocalTags(selectedTags);
    }, [selectedTags]);

    const filteredTags = useMemo(() => {
        if (!tags) return [];
        const normalizedQuery = searchQuery.toLowerCase();

        return tags
            .filter((tag) => tag.title.toLowerCase().includes(normalizedQuery))
            .sort((a, b) => {
                const aSelected = localTags.some((t) => t.id === a.id);
                const bSelected = localTags.some((t) => t.id === b.id);
                return aSelected === bSelected ? 0 : aSelected ? -1 : 1;
            });
    }, [tags, searchQuery, localTags]);

    const selectedFilteredTags = useMemo(
        () => filteredTags.filter((tag) => localTags.some((t) => t.id === tag.id)),
        [filteredTags, localTags],
    );

    const unselectedFilteredTags = useMemo(
        () => filteredTags.filter((tag) => !localTags.some((t) => t.id === tag.id)),
        [filteredTags, localTags],
    );

    const handleTagToggle = (tag: FileTag) => {
        const updated = localTags.some((t) => t.id === tag.id)
            ? localTags.filter((t) => t.id !== tag.id)
            : [...localTags, tag];
        setLocalTags(updated);
        onSelectTags(updated);
    };

    const handleAddTag = () => {
        if (!params.wellId) return;
        addTagMutation.mutate({title: searchQuery, wellId: params.wellId});
        setSearchQuery('');
    };

    // Клик, чтобы узнать, к скольким документам привязан тег
    const handleDeleteClick = useCallback(async (tag: FileTag) => {
        const isUsed = await isUsedFileTag(tag.id);
        setUsedTag(isUsed);
    }, []);

    // Удаление самого тега
    const handleDeleteTag = (tag: FileTag) => {
        deleteTagMutation.mutate(tag.id, {
            onSuccess: () => {
                const updated = localTags.filter((t) => t.id !== tag.id);
                setLocalTags(updated);
                onSelectTags(updated);
                refetch?.();
            },
        });
    };

    if (!isOpen) {
        return null;
    }

    return (
        <Stack
            direction='column'
            sx={{
                height: '100%',
                width: '100%',
            }}
        >
            <Stack
                direction='column'
                sx={{
                    position: 'sticky',
                    top: 0,
                }}
            >
                <Stack
                    direction='row'
                    justifyContent='space-between'
                    alignItems='center'
                    sx={{mb: 1}}
                >
                    <Typography>{t('fileUploadTagsSidebar.tagsToDocument')}</Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>

                <SearchInput
                    label={t('fileUploadTagsSidebar.findTags')}
                    value={searchQuery}
                    onInput={(val) => setSearchQuery(val)}
                    width='100%'
                    size='small'
                    variant='outlined'
                />
            </Stack>
            <Stack
                direction='column'
                spacing={1}
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    p: '8px 12px',
                }}
            >
                {!!searchQuery.trim() && (
                    <Stack
                        direction='row'
                        justifyContent='flex-start'
                    >
                        <Button
                            size='small'
                            onClick={handleAddTag}
                            startIcon={<AddIcon />}
                        >
                            {t('fileUploadTagsSidebar.createNewTag')}
                        </Button>
                    </Stack>
                )}
                {selectedFilteredTags.map((tag) => (
                    <TagListRow
                        key={tag.id}
                        tag={tag}
                        checked
                        onToggle={handleTagToggle}
                        onDeleteClick={handleDeleteClick}
                        onDeleteConfirm={handleDeleteTag}
                        usedTag={usedTag}
                    />
                ))}

                {selectedFilteredTags.length > 0 && unselectedFilteredTags.length > 0 && (
                    <Divider sx={{my: 1}} />
                )}
                {unselectedFilteredTags.map((tag) => (
                    <TagListRow
                        key={tag.id}
                        tag={tag}
                        checked={false}
                        onToggle={handleTagToggle}
                        onDeleteClick={handleDeleteClick}
                        onDeleteConfirm={handleDeleteTag}
                        usedTag={usedTag}
                    />
                ))}
            </Stack>
        </Stack>
    );
};
